
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { notify } from '@/utils/notification';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { GroupEvent } from './GroupEventsManager';
import LocationAutocomplete, { LocationResult } from '@/components/events/LocationAutocomplete';
import { TherapyTagSelector } from '@/components/dashboard/practitioner/services/TherapyTagSelector';

interface EventFormProps {
  event: GroupEvent | null;
  onClose: () => void;
  onSuccess: () => void;
}

const COMMON_TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Kolkata', 'Asia/Dubai',
  'Australia/Sydney', 'Pacific/Auckland',
];

const EventForm: React.FC<EventFormProps> = ({ event, onClose, onSuccess }) => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const isEditing = !!event;

  const [saving, setSaving] = useState(false);
  const [therapyTagIds, setTherapyTagIds] = useState<string[]>((event as any)?.therapy_tag_ids || []);
  const [customTherapyTags, setCustomTherapyTags] = useState<string[]>([]);
  const [form, setForm] = useState({
    title: event?.title_en || event?.title || '',
    description: event?.description_en || event?.description || '',
    event_date: event?.event_date ? new Date(event.event_date).toISOString().slice(0, 16) : '',
    duration_minutes: event?.duration_minutes || 60,
    location_type: event?.location_type || 'virtual',
    location_details: event?.location_details || '',
    location_lat: (event as any)?.location_lat || null as number | null,
    location_lng: (event as any)?.location_lng || null as number | null,
    location_city: (event as any)?.location_city || '',
    location_country: (event as any)?.location_country || '',
    timezone: (event as any)?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    price: event?.price || 0,
    currency: event?.currency || 'USD',
    min_participants: event?.min_participants || 2,
    max_participants: event?.max_participants || 20,
    tags: (event?.tags || []).join(', '),
    service_track: event?.service_track || 'wellness',
    cover_image_url: event?.cover_image_url || '',
    registration_deadline: event?.registration_deadline ? new Date(event.registration_deadline).toISOString().slice(0, 16) : '',
  });

  const handleChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationResult = (result: LocationResult | null, rawText: string) => {
    if (result) {
      setForm(prev => ({
        ...prev,
        location_details: result.address,
        location_lat: result.lat,
        location_lng: result.lng,
        location_city: result.city,
        location_country: result.country,
        timezone: result.timezone || prev.timezone,
      }));
    } else {
      setForm(prev => ({ ...prev, location_details: rawText }));
    }
  };

  const handleSubmit = async (status: 'draft' | 'published') => {
    if (!user?.id) return;
    if (!form.title) {
      notify.error(t('error'), t('events.form.titleRequired'));
      return;
    }
    if (!form.event_date) {
      notify.error(t('error'), t('events.form.dateRequired'));
      return;
    }

    setSaving(true);
    try {
      const eventDate = new Date(form.event_date);
      const endDate = new Date(eventDate.getTime() + form.duration_minutes * 60000);
      // Merge custom therapy tags into tags array
      const manualTags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
      const tags = [...manualTags, ...customTherapyTags];

      const payload = {
        practitioner_id: user.id,
        title: form.title,
        title_en: form.title || null,
        title_zh: null,
        description: form.description,
        description_en: form.description || null,
        description_zh: null,
        event_date: eventDate.toISOString(),
        end_date: endDate.toISOString(),
        duration_minutes: form.duration_minutes,
        location_type: form.location_type,
        location_details: form.location_details || null,
        location_lat: form.location_lat,
        location_lng: form.location_lng,
        location_city: form.location_city || null,
        location_country: form.location_country || null,
        timezone: form.timezone || null,
        price: form.price,
        currency: form.currency,
        min_participants: form.min_participants,
        max_participants: form.max_participants,
        tags,
        therapy_tag_ids: therapyTagIds,
        service_track: form.service_track,
        cover_image_url: form.cover_image_url || null,
        registration_deadline: form.registration_deadline ? new Date(form.registration_deadline).toISOString() : null,
        status,
      };

      if (isEditing && event) {
        const { error } = await supabase
          .from('group_events')
          .update(payload)
          .eq('id', event.id);
        if (error) throw error;
        notify.success(t('events.form.updated'));
      } else {
        const { error } = await supabase
          .from('group_events')
          .insert(payload);
        if (error) throw error;
        notify.success(t('events.form.created'));
      }
      onSuccess();
    } catch (err) {
      console.error('Failed to save event:', err);
      notify.error(t('error'), t('events.form.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const showLocationSearch = form.location_type === 'in_person' || form.location_type === 'hybrid';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onClose}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-xl font-semibold">
          {isEditing ? t('events.form.editTitle') : t('events.form.createTitle')}
        </h2>
      </div>

      <div className="grid gap-6 max-w-2xl">
        {/* Title */}
        <div className="space-y-2">
          <Label>{t('events.form.title')}</Label>
          <Input value={form.title} onChange={e => handleChange('title', e.target.value)} placeholder={t('events.form.titlePlaceholder')} />
        </div>
        {/* Description */}
        <div className="space-y-2">
          <Label>{t('events.form.description')}</Label>
          <Textarea value={form.description} onChange={e => handleChange('description', e.target.value)} placeholder={t('events.form.descriptionPlaceholder')} rows={4} />
        </div>
        {/* Date & Duration */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('events.form.dateTime')}</Label>
            <Input type="datetime-local" value={form.event_date} onChange={e => handleChange('event_date', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t('events.form.duration')}</Label>
            <Input type="number" min={15} value={form.duration_minutes} onChange={e => handleChange('duration_minutes', parseInt(e.target.value) || 60)} />
          </div>
        </div>
        {/* Registration Deadline */}
        <div className="space-y-2">
          <Label>{t('events.form.registrationDeadline')}</Label>
          <Input type="datetime-local" value={form.registration_deadline} onChange={e => handleChange('registration_deadline', e.target.value)} />
          <p className="text-xs text-muted-foreground">{t('events.form.registrationDeadlineHint')}</p>
        </div>
        {/* Timezone */}
        <div className="space-y-2">
          <Label>{t('events.form.timezone')}</Label>
          <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.timezone} onChange={e => handleChange('timezone', e.target.value)}>
            {COMMON_TIMEZONES.map(tz => (
              <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
            ))}
            {!COMMON_TIMEZONES.includes(form.timezone) && (
              <option value={form.timezone}>{form.timezone.replace(/_/g, ' ')}</option>
            )}
          </select>
        </div>
        {/* Location */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('events.form.locationType')}</Label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.location_type} onChange={e => handleChange('location_type', e.target.value)}>
              <option value="virtual">{t('events.form.virtual')}</option>
              <option value="in_person">{t('events.form.inPerson')}</option>
              <option value="hybrid">{t('events.form.hybrid')}</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>{t('events.form.locationDetails')}</Label>
            {showLocationSearch ? (
              <LocationAutocomplete
                value={form.location_details}
                onChange={handleLocationResult}
                placeholder={t('events.form.searchAddress')}
              />
            ) : (
              <Input value={form.location_details} onChange={e => handleChange('location_details', e.target.value)} placeholder={t('events.form.virtualLinkPlaceholder')} />
            )}
          </div>
        </div>
        {/* Price, Currency, Min, Max */}
        <div className="grid grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label>{t('events.form.price')}</Label>
            <Input type="number" min={0} step={0.01} value={form.price} onChange={e => handleChange('price', parseFloat(e.target.value) || 0)} />
          </div>
          <div className="space-y-2">
            <Label>{t('events.form.currency')}</Label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.currency} onChange={e => handleChange('currency', e.target.value)}>
              <option value="USD">USD</option>
              <option value="CNY">CNY</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>{t('events.form.minParticipants')}</Label>
            <Input type="number" min={1} value={form.min_participants} onChange={e => handleChange('min_participants', parseInt(e.target.value) || 2)} />
          </div>
          <div className="space-y-2">
            <Label>{t('events.form.maxParticipants')}</Label>
            <Input type="number" min={1} value={form.max_participants} onChange={e => handleChange('max_participants', parseInt(e.target.value) || 20)} />
          </div>
        </div>
        {/* Track */}
        <div className="space-y-2">
          <Label>{t('events.form.serviceTrack')}</Label>
          <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.service_track} onChange={e => handleChange('service_track', e.target.value)}>
            <option value="wellness">{t('events.form.wellness')}</option>
            <option value="clinical">{t('events.form.clinical')}</option>
          </select>
        </div>
        {/* Therapy Tags */}
        <div className="space-y-2">
          <Label>{t('events.form.therapyTags')}</Label>
          <p className="text-xs text-muted-foreground">{t('events.form.therapyTagsHint')}</p>
          <TherapyTagSelector
            value={therapyTagIds}
            onChange={setTherapyTagIds}
            customTags={customTherapyTags}
            onCustomTagsChange={setCustomTherapyTags}
          />
        </div>
        {/* Free-text Tags */}
        <div className="space-y-2">
          <Label>{t('events.form.tags')}</Label>
          <Input value={form.tags} onChange={e => handleChange('tags', e.target.value)} placeholder={t('events.form.tagsPlaceholder')} />
        </div>
        {/* Cover Image URL */}
        <div className="space-y-2">
          <Label>{t('events.form.coverImage')}</Label>
          <Input value={form.cover_image_url} onChange={e => handleChange('cover_image_url', e.target.value)} placeholder="https://..." />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('events.form.cancel')}</Button>
          <Button variant="secondary" onClick={() => handleSubmit('draft')} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('events.form.saveAsDraft')}
          </Button>
          <Button onClick={() => handleSubmit('published')} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('events.form.publish')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventForm;
